/*! Goldtop - "Change size or colour?" inside the slide cart. Every width.
    Loaded on every page from layout/theme.liquid, because the slide cart is on
    every page. Styles in assets/gt-slidecart-size.css.

    ------------------------------------------------------------------
    WHAT THIS REPLACED, AND WHY.

    The first version, 26 Sep, put a size dropdown under the quantity selector
    in each cart line. It worked, and Joe's verdict the same day was that it was
    "janky or slow to respond", and that "the extra depth makes the images much
    more narrowly cropped". Both complaints were fair and they have the same
    root:

      A DROPDOWN PROMISES TO BE INSTANT. Changing a cart line's variant cannot
      be. Shopify has no call that changes a line's variant, so it is
      add-the-new-one then remove-the-old-one - two round trips, timed at about
      750ms on the preview. Under a control that looks like a native select,
      750ms reads as broken. Under a sheet the shopper confirms, the same 750ms
      reads as the change being applied. The fix was never going to be shaving
      milliseconds; it was to stop making a promise the cart cannot keep.

      THE ROW COST HEIGHT. A second control under the quantity selector made
      every line taller, and the app's line image is centred in that box, so it
      gained dead space above and below and read as narrowly cropped. A text
      link on a row that already exists costs nothing.

    So: a quiet "Change size or colour?" link to the right of the line's own
    "S - 7.5" - Black" statement, opening a sheet with every option on it.

    ------------------------------------------------------------------
    WHY THE DROPDOWNS INSIDE THE SHEET ARE GONE TOO, 27 Sep.

    The sheet shipped on 27 Sep with one <select> per option, and unbuyable
    values disabled. Joe found the dead end within the day, and it is worth
    writing down because it is a property of disabling, not of selects:

      Cart holds Black / S. Black is sold out in every other size. Brown has M
      and L. Open the sheet: the Size select has every size but S disabled,
      because each one is judged against the colour you are already on. There
      is no sequence of choices that reaches Brown / L. The shopper can see the
      sizes exist and cannot get to them. That is worse than not offering the
      control at all.

    Disabling assumes the shopper picks in an order that is always valid. They
    do not. They pick the thing they want and expect the rest to follow. So
    NOTHING IS DISABLED IN THE ROWS ANY MORE. Every value stays tappable,
    always; the sheet says what the chosen combination is; and the UPDATE
    button is the single gate. Joe asked for exactly this - "people would be
    able to select out of stock variants, which needs to declare out of stock
    but not enable confirmation of the change".

    Walk the same cart through it now: tap L. Black / L is sold out, the note
    says so, UPDATE is dead - and the Colour row has just re-marked itself
    against L, so Brown is the one colour still lit. Tap Brown. Done, in two
    taps, with the way out shown rather than explained.

    ------------------------------------------------------------------
    IT IS THE PDP'S OWN CONTROL, NOT A LOOKALIKE. Second pass, same day.

    The first version of this sheet invented its own treatment and Joe's note
    was that it should be the product page's: "the interactions and colouring /
    transparency settings etc should match what the normal PDP for a product
    has, where there is a strikethrough on out of stock variants". Fair, and
    the right instinct - a shopper who has just chosen a size on the PDP should
    not have to learn a second control to change it in the cart.

    So the geometry, the colours and the class names below were read off the
    running PDP rather than chosen. The stylesheet's header carries the
    measurements; what matters here is the behaviour:

      NOTHING IS DISABLED. Every value is tappable at all times. The PDP is the
      same: its sold-out chips keep pointer-events and a pointer cursor, and
      gt-pdp-v2-x.js carries a capture-phase handler specifically so a sold-out
      chip can still be chosen.

      is-on AND is-gone ARE THE ONLY TWO STATE CLASSES, spelled as the PDP
      spells them, and they combine - standing on a sold-out size is a normal
      state, not an error.

      WHICH VALUES GET is-gone IS ASYMMETRIC, and that asymmetry is the PDP's,
      established by driving its buy bar through four states and reading the
      classes back:

        THE SIZE RAIL IS JUDGED AGAINST THE WHOLE PRODUCT. On the Predator only
        L is ever marked, because L is the one size sold out in all six
        colours. M is sold out in Black and is NOT marked while Black is
        selected.

        EVERY OTHER RAIL IS JUDGED AGAINST THE CURRENT SELECTION. The colour
        swatches re-mark on every size change - four gone at M, three at XXL,
        two at S, each matching the storefront exactly.

      It is a good rule rather than an accident. Size is the axis the shopper
      has already settled, so it holds still; colour is the one they are
      shopping, so it answers against their size. It is also what breaks the
      dead end this sheet was rebuilt for: from Black / S, tap M, and the
      colour rail immediately shows which colours M can still be had in.

    Availability is read from the storefront's own product json, which - unlike
    the Admin API - already accounts for which locations the online store sells
    from. That was the Neck Tube lesson: the Admin API said four in stock, the
    storefront said sold out, and the storefront was right. It bit again here:
    the Admin API reports M - 8" / Black on the Predator as available and
    /cart/add.js returns 422 "already sold out" for it.

    ------------------------------------------------------------------
    SWATCHES FOR COLOUR, CHIPS FOR EVERYTHING ELSE.

    A row is drawn as swatches when the option's name contains "colour" or
    "color", matching the theme's own swatch trigger; everything else - Size,
    Style, Length - is drawn as text chips. Same rule the PDP uses, so a
    shopper meets one visual language on the product page and in the cart.

    THE SWATCH IMAGE IS THE SHOP FILE, exactly as the PDP resolves it:
    /cdn/shop/files/<value handled>.png, asked for at 60x60. Verified against
    the live PDP, which renders .gt-swatchchip with an inline background-image
    of files/black_50x50.png, and against the Files library, which holds
    waxed-brown.png and racing-green.png under those names.

    That file does not exist for every value, so there are two fallbacks and
    they are ordered on purpose. If the flat file 404s, the swatch takes the
    VARIANT'S OWN PHOTOGRAPH, cropped square - a picture of the thing in that
    colour is a better answer than a guess at the colour. If there is no
    photograph either, a CSS colour keyword taken from the last word of the
    value ("Racing Green" -> green) sits underneath, and where that is not a
    real keyword the browser drops it and the neutral tile in the stylesheet
    shows through. Nothing ever renders as a blank hole.

    ------------------------------------------------------------------
    THE SWAP, AND WHY IT IS STILL TWO CALLS.

    /cart/update.js can do it in one, and it was measured at 478ms against
    1,127ms for the old three-call path. It is not used, for two reasons found
    by testing rather than by reading:

      1. Pointed at a variant the storefront reports as unavailable, update.js
         returned 200 and PUT IT IN THE CART. /cart/add.js refuses the same
         thing. Using update.js would trade a clean failure for a silent
         oversell that only surfaces at checkout.
      2. update.js keyed by variant id cannot carry line item properties, so an
         Assortion add-on or an engraving note attached to the line would be
         dropped silently - changing what the customer ordered.

    So the sequence is add-then-remove, and the ORDER MATTERS: if the add fails
    the cart is untouched and the sheet stays open with the choice intact. The
    other way round, a failed add leaves the customer with nothing where their
    jacket was.

    It is two calls rather than three because /cart.js is fetched when the SHEET
    OPENS, while the shopper is reading, rather than at the moment they confirm.
    That is where the quantity, the properties and any selling plan come from -
    the cart, not the DOM.

    HOW THE DRAWER IS REFRESHED. SlideCart HQ exposes SLIDECART_UPDATE() on
    window. Verified on the preview: a raw fetch to a cart endpoint does NOT
    refresh the drawer on its own - the cart held an item while the drawer still
    showed none - and SLIDECART_UPDATE() brings it into line.

    THE APP OWNS THE DOM AND REBUILDS IT after every cart mutation, so the link
    is re-injected by an observer and every function here is idempotent.

    ------------------------------------------------------------------
    THE HANG, 27 Sep. Joe: "when I click to confirm, the page hangs / freezes."

    Reproduced by sampling the button every 25ms from the instant the sheet
    opens. For the first ~120ms it reads UPDATE and is ENABLED, on the variant
    that is already in the cart:

        0ms  go=UPDATE/ON        <- tap here and nothing happens, ever
      125ms  go=NO CHANGE/off

    The cause is an ordering one. The button's state is computed from
    state.line, the cart's own record of this line, which openSheet fetches
    while the shopper reads. Until it lands state.line is null, so "is this the
    same variant?" answers false, so the button enables - and confirmSheet's
    own guard then returns silently on the same null. Enabled control, dead
    tap, no message. On a phone on a slow connection that window is not 120ms,
    it is however long /cart.js takes.

    Three things follow from it, all fixed here:

      1. THE BUTTON NOW WAITS FOR THE LINE. `known` gates it. It reads UPDATE
         and stays disabled until the cart answers, which is honest: it is
         about to become tappable.
      2. CONFIRM NO LONGER FAILS SILENTLY. Every path ends in either a closed
         sheet or a message in the status line, and there is a 12 second
         timeout so a request that never returns cannot leave the sheet on
         "UPDATING..." for good.
      3. THE TWO FAILURES ARE DIFFERENT AND ARE SAID DIFFERENTLY. If the ADD
         fails the cart is untouched and the shopper can try again. If the add
         succeeds and the REMOVE fails, the cart now holds both lines - seen
         once in testing, and the old code closed the sheet as though all was
         well. It now says so and refreshes the drawer so the duplicate is
         visible rather than discovered at checkout.

    Also: busy is cleared and the button repainted BEFORE the sheet closes.
    closeSheet nulls state, and the old order left the label reading
    "UPDATING..." on a sheet that had already gone.

    ------------------------------------------------------------------
    THE STATUS LINE ALWAYS HAS SOMETHING IN IT, and that is a layout fix
    rather than a copy decision. Joe: "there is some shifting on the selectors
    when we try to change the variant, and that is caused by shifting upwards
    when the sold out text appears."

    He is right: the note used to be hidden when there was nothing to say, so
    it collapsed and everything below it moved every time the shopper landed
    on or left a sold-out pair - which is exactly the moment they are aiming
    at a 26px swatch. So the line is always present, and when there is no
    warning it carries the PDP's traffic light instead.

    IT SAYS WHAT THE PDP SAYS, using the theme's own strings and the theme's
    own rule, read out of snippets/product-inventory.liquid:

        low stock  iff  inventory_management == "shopify"
                   and  quantity > 0
                   and  quantity <= threshold
                   and  inventory_policy != "continue"

    Labels come from window.theme.strings - inStockLabel, oneStockLabel,
    otherStockLabel - so they stay in step with the locale file rather than
    being retyped here.

    AND NO FULL STOP ON ANY OF THE THREE. Joe, 28 Sep: the sold-out line should
    read "Black is currently sold out" rather than "Black is sold out.", to
    match the traffic light beside it - "In stock, ready to ship" has never
    carried one. "Currently" is the substantive half of that: sold out is a
    state, not a verdict on the product, and a shopper who reads it as
    temporary is a shopper who comes back. The two-sentence failure messages
    further down keep their stops, because dropping the last one from a pair of
    sentences reads as a typo rather than as a style.

    WHERE THE COUNT COMES FROM. Not from /products/<handle>.js, which carries
    `available` and no quantity at all - the key is simply absent. Impulse
    renders the counts into .js-variant-inventory-data on the product page
    only, and the slide cart is on every page but that one. So there is a
    second, tiny endpoint: templates/product.gt-stock.liquid, reached at
    /products/<handle>?view=gt-stock, which returns a few hundred bytes of
    JSON. That file's own header explains itself. If it is missing or fails,
    stockLine falls back to the plain in-stock label and nothing breaks.

    ONE HONEST GAP. The PDP's threshold is a BLOCK setting on the
    inventory_status block, and it differs per product template - 2 on the
    default, 6 on gloves, 4 on the jackets, 8 on the sheepskin. Liquid block
    settings are section-scoped, so a standalone template cannot read them,
    and the view returns the theme-level setting instead (currently 2). That
    errs towards saying "In stock" where the PDP would say "Low stock", which
    is the safe direction - the cart never invents scarcity the product page
    does not claim. Consolidating those 23 block settings into the one theme
    setting would close the gap and simplify the admin at the same time.

    ------------------------------------------------------------------
    THE PAGE IS NOT FROZEN, IT IS COVERED. 27 Sep, third report.

    Joe, after two wrong diagnoses from me: "Clicking to submit a change in
    colour/size still freezes the page and pop up bar." The words that finally
    gave it away are AND POP UP BAR - the sticky buy bar is a separate fixed
    element near the bottom of the screen, and nothing that hangs JavaScript
    would take out the page and that bar and leave the browser otherwise
    responsive. Something is lying on top of everything.

    MEASURED, by asking what is actually under the finger after the confirm,
    with document.elementFromPoint, every 120ms:

        0ms .. 720ms   DIV.gt-sc-sheet__scrim    open=true   lock=true
      720ms .. 960ms   DIV.gt-sc-sheet__scrim    open=false  lock=false
      960ms ..         the drawer, as it should be

    Two separate faults in that trace.

    ONE: THE SCRIM KEEPS CATCHING TAPS AFTER THE SHEET HAS CLOSED. From 720ms
    the sheet is closed by every measure - is-open gone, scroll lock released,
    panel transformed off the bottom - and the root element is still
    position:fixed inset:0 at the top of the z-order. The scrim's opacity has
    animated to 0, and OPACITY 0 STILL HIT-TESTS. Only the `hidden` attribute
    takes it out of the layer, and that was applied on a 260ms timeout, so
    there was always a quarter of a second of invisible glass over the whole
    page. Fixed with one CSS rule rather than a shorter timer: the sheet takes
    no pointer events at all unless it is open.

    TWO, AND THIS IS THE ONE THAT DOES NOT RECOVER: nothing cancelled a queued
    open(). openSheet schedules the class that reveals the panel twice, on a
    double rAF and on a 60ms timer, because a hidden tab does not run rAF. If
    either of those fires AFTER the sheet has been closed - a frame the phone
    delayed, a tab that came back - is-open goes back on, the hide timeout
    then sees is-open and declines to set `hidden`, and the page is left under
    a full-screen transparent layer with state === null, so nothing inside it
    works either. That is a page that stays frozen until reload, and it
    matches the report exactly.

    Both are now generation-guarded: every open and every close takes a ticket,
    and a callback holding a stale ticket does nothing.

    WHY THE EARLIER TESTS ALL PASSED. They asserted on state - cart contents,
    class lists, button labels - and every one of those was correct throughout.
    The page was wrong in a way no assertion about state can see. Asking what
    is under a given pixel is a different question, and it is the one that
    should have been asked first for anything involving a fixed overlay.

    ------------------------------------------------------------------
    THE BUTTON WAS UNDER SOMETHING ELSE. 27 Sep, fourth report, and the
    screenshot is what finally showed it.

    Joe's photo: the sheet open on the BSMC glove, size and colour chosen,
    "In stock, ready to ship" - and then the panel just stops and Shopify's
    black preview bar begins. UPDATE is not on screen. He taps where it should
    be, nothing happens, the page is scroll-locked because the sheet IS open,
    and the only way out is a reload. Every word of "freezes the page and pop
    up bar" is accurate; nothing was frozen at all.

    MEASURED at 375x812:

      button        top 744, bottom 792
      elementFromPoint at its centre -> IFRAME#PBarNextFrame
      DIV#PBarNextFrameWrapper   position:fixed   rect 744..812

    and it is not alone down there:

      IFRAME#button_CSYAAi... (AnswerAI chat launcher)  z 1000001  rect 742..802

    The preview bar is preview-only, so the live site would not have shown
    this exact collision - but the bottom 70px of a phone viewport is where
    every floating widget lives, and a modal that puts its only action flush
    against bottom:0 will keep meeting one. Special-casing Shopify's bar would
    fix today and not tomorrow.

    SO THE SHEET ASKS, RATHER THAN ASSUMES. Once it is open and the slide-in
    has finished, it does exactly what found this bug: elementFromPoint at the
    centre of the button. If the answer is not the button, whatever IS there
    is measured and the panel gains enough bottom padding to clear it, and it
    asks again. A cap of three passes stops any chance of a fight with a
    widget that moves.

    It costs one forced layout per sheet open, which is affordable; it is the
    continuous ones that cost a phone its battery. Re-run on resize and
    orientation change, because a rotated phone has different chrome.

    ------------------------------------------------------------------
    28 Sep, DESKTOP. Joe: "roll out on web the change style/colour? question
    link and also the button / swatches for the you may also like products -
    both on the slide cart on web".

    The two width gates are gone: the link is built on every cart line at every
    width, and the sheet opens at every width. Nothing else changed, because
    nothing else needed to - the drawer's markup is the same at 500px as it is
    at 375px, and every rule for the link and the sheet was already written
    against the app's own classes rather than against a phone.

    TWO THINGS ARE STILL WIDTH-GATED, ON PURPOSE. The cart line's IMAGE rules
    stay in the stylesheet's mobile block, because the 30% clamp and the 110px
    cliff were measured against a 375px drawer and mean nothing in a 500px one;
    desktop cart lines were never complained about. And gt-rec-add.js keeps its
    own breakpoint, so the "Pairs well with" cards on a product page are
    untouched on desktop - that surface was not part of this rollout.

    WHERE THE SHEET SITS ON DESKTOP. The drawer is a 500px column pinned to the
    right, measured with it open at two viewports, so the sheet takes that
    column rather than the width of the screen. sizeToDrawer() writes the
    measured width into --gt-sheet-w and the stylesheet does the rest; the 500
    in the CSS is only the fallback for having no drawer to measure.

    ------------------------------------------------------------------
    28 Sep, THE UPCHARGES. Joe: "add the '+50' and other increase in cost
    indicators to the size 50 and 52 jacket selectors (just like on the main
    PDP), and also for other items which have variable prices for the different
    options... on both web and mobile".

    NO PRICE IS FORMATTED HERE, AND THAT RULE IS UNCHANGED. Every money string
    on this site is rendered by Liquid; reimplementing the shop's currency
    format in JavaScript gets it wrong for somebody. So the upcharge arrives
    finished from templates/product.gt-stock.liquid - the same endpoint the
    traffic light already uses - and this file only puts it on screen.

    A CHIP CARRIES ITS OWN UPCHARGE. A SWATCH CANNOT: it is 26px of colour with
    nowhere to put text, and the PDP has no answer for that case either, having
    only ever emitted deltas for its size rail. Two products in these sheets
    are priced by colour, so a swatch rail's upcharge goes on the summary line
    under the title, beside the colour's name - the one place the sheet already
    spells a colour out in words.

    PURE ASCII, working rule 18.
*/
(function () {
  "use strict";

  if (window.__gtScSize) return;
  window.__gtScSize = true;

  /* Kept only as a resize signal now that neither build() nor sweep() gates on
     it: crossing the breakpoint changes the stylesheet's image rules, and a
     re-sweep after that is cheap and idempotent. */
  var MQ = window.matchMedia("(max-width: 768px)");
  var MARK = "gt-sc-change";
  var COLOUR = /colou?r/i;
  var SIZE = /size/i;
  /* The buy bar's own summary separator - a middot, two spaces each side, as
     the bar has it. Built from its character code rather than written as a
     unicode escape, and that is a transport fix rather than a style one: the
     theme upload endpoint DECODES unicode escape sequences in the body it is
     given, so a literal one in this source arrived as the character itself -
     four bytes short and, worse, non-ASCII in a file that must not be
     (working rule 18). fromCharCode says the same thing with nothing for the
     transport to eat. */
  var SEP = "  " + String.fromCharCode(183) + "  ";
  var cache = {};
  var sheet = null, state = null, busy = false;
  /* Every open and every close takes a ticket. A queued callback that is
     holding a stale one does nothing - see the header. */
  var gen = 0;

  function q(s, r) { try { return (r || document).querySelector(s); } catch (e) { return null; } }
  function qa(s, r) { try { return [].slice.call((r || document).querySelectorAll(s)); } catch (e) { return []; } }

  function handleOf(item) {
    var a = q("a.item-image-anchor", item) || q("a[href*='/products/']", item);
    var href = a ? (a.getAttribute("href") || "") : "";
    var m = href.match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  function product(handle) {
    if (!handle) return Promise.resolve(null);
    if (cache[handle]) return Promise.resolve(cache[handle]);
    var pr = fetch("/products/" + encodeURIComponent(handle) + ".js", { credentials: "same-origin" })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) { cache[handle] = j; return j; })
      .catch(function () { cache[handle] = null; return null; });
    cache[handle] = pr;
    return pr;
  }

  /* options arrive as strings on older shops and objects on newer ones. */
  function optionNames(p) {
    return (p.options || []).map(function (o) {
      return String((o && o.name != null) ? o.name : o || "");
    });
  }

  function variantByValues(p, vals) {
    var i, j, v, ok;
    for (i = 0; i < p.variants.length; i++) {
      v = p.variants[i]; ok = true;
      for (j = 0; j < vals.length; j++) if (v.options[j] !== vals[j]) { ok = false; break; }
      if (ok) return v;
    }
    return null;
  }

  /* Per option index, the distinct values in the order the variants declare
     them, each carrying whether ANY buyable variant on the product has it.
     That flag is what separates a value that is gone for good from one that is
     merely wrong against the current selection - see the header. Built once
     when the sheet opens, because it does not depend on what is selected. */
  function valueIndex(p) {
    var rows = [];
    p.variants.forEach(function (v) {
      (v.options || []).forEach(function (val, i) {
        if (val == null || val === "") return;
        if (!rows[i]) rows[i] = { order: [], alive: {}, photo: {} };
        var r = rows[i];
        if (r.alive[val] === undefined) { r.order.push(val); r.alive[val] = false; }
        if (v.available) r.alive[val] = true;
        if (!r.photo[val] && v.featured_image && v.featured_image.src) r.photo[val] = v.featured_image.src;
      });
    });
    return rows;
  }

  /* The link's wording, from the product's own option names. Two is the most
     that fits the row comfortably at 375px; past that it says "options". */
  function linkLabel(p) {
    var n = optionNames(p).filter(Boolean);
    if (!n.length) return "Change options?";
    if (n.length === 1) return "Change " + n[0].toLowerCase() + "?";
    if (n.length === 2) return "Change " + n[0].toLowerCase() + " or " + n[1].toLowerCase() + "?";
    return "Change options?";
  }

  function sized(url, w) {
    if (!url) return "";
    if (/[?&]width=/.test(url)) return url;
    return url + (url.indexOf("?") > -1 ? "&" : "?") + "width=" + w;
  }

  /* Liquid's `handle` filter, for the ASCII the catalogue actually uses. */
  function handleize(s) {
    return String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  }

  function refresh() {
    try { if (typeof window.SLIDECART_UPDATE === "function") window.SLIDECART_UPDATE(); } catch (e) {}
  }

  /* ---------------------------------------------------------------- */
  /* The traffic light. See the header for why this needs its own tiny
     endpoint rather than reading /products/<handle>.js.               */

  var stock = {};

  /* The upcharges. They arrive from the same endpoint as the stock, as a flat
     list of [option index, value, delta] triples, and are turned into a lookup
     once on arrival. EVERY DELTA IS ALREADY A MONEY STRING, rendered by
     Liquid - see templates/product.gt-stock.liquid. Nothing here parses one. */
  function deltaMap(j) {
    var m = [], rows = (j && j.d) || [], i, r, k;
    for (i = 0; i < rows.length; i++) {
      r = rows[i];
      if (!r || r.length < 3) continue;
      k = Number(r[0]);
      if (!m[k]) m[k] = {};
      m[k][r[1]] = String(r[2]);
    }
    return m;
  }

  function deltaFor(i, val) {
    var m = state && state.delta;
    return (m && m[i] && m[i][val]) || "";
  }

  function stockFor(handle) {
    if (!handle) return Promise.resolve(null);
    if (stock[handle] !== undefined) return Promise.resolve(stock[handle]);
    var pr = fetch("/products/" + encodeURIComponent(handle) + "?view=gt-stock",
                   { credentials: "same-origin" })
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (t) {
        var j = null;
        try { j = JSON.parse(String(t).trim()); } catch (e) { j = null; }
        stock[handle] = j;
        return j;
      })
      .catch(function () { stock[handle] = null; return null; });
    stock[handle] = pr;
    return pr;
  }

  function themeString(key, fallback) {
    try {
      var s = window.theme && window.theme.strings;
      if (s && typeof s[key] === "string" && s[key]) return s[key];
    } catch (e) {}
    return fallback;
  }

  /* The theme's own rule, from snippets/product-inventory.liquid. A variant
     that is not tracked has no meaningful quantity, so it gets the plain
     label rather than a made up count. */
  function stockLine(v) {
    var plain = themeString("inStockLabel", "In stock, ready to ship");
    var data = state && state.stock;
    if (!v || !data || !data.v) return plain;

    var row = null, i;
    for (i = 0; i < data.v.length; i++) if (data.v[i].id === v.id) { row = data.v[i]; break; }
    if (!row || row.m !== "shopify" || row.p === "continue") return plain;

    var qty = Number(row.q);
    var threshold = Number(data.th);
    if (!(qty > 0) || !(threshold >= 0) || qty > threshold) return plain;

    var t = qty === 1
      ? themeString("oneStockLabel", "Quick, only [count] left!")
      : themeString("otherStockLabel", "Low stock - Only [count] items left!");
    return t.replace("[count]", String(qty));
  }

  /* ---------------------------------------------------------------- */
  /* The sheet. One per page, built on first use, reused after.        */

  function buildSheet() {
    if (sheet) return sheet;
    var el = document.createElement("div");
    el.className = "gt-sc-sheet";
    el.setAttribute("hidden", "hidden");
    el.innerHTML =
      '<div class="gt-sc-sheet__scrim" data-gt-close></div>' +
      '<div class="gt-sc-sheet__panel" role="dialog" aria-modal="true">' +
        '<button type="button" class="gt-sc-sheet__close" data-gt-close aria-label="Close">' +
          '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M5 5l14 14M19 5L5 19" fill="none" stroke="currentColor" stroke-width="1.4"/></svg>' +
        '</button>' +
        '<div class="gt-sc-sheet__head">' +
          '<div class="gt-sc-sheet__thumb"><img class="gt-sc-sheet__img" alt="" /></div>' +
          '<div>' +
            '<div class="gt-sc-sheet__title"></div>' +
            '<span class="gt-sc-sheet__sum"></span>' +
          '</div>' +
        '</div>' +
        '<div class="gt-sc-sheet__opts"></div>' +
        '<p class="gt-sc-sheet__note" role="status" aria-live="polite"></p>' +
        '<button type="button" class="gt-sc-sheet__go">UPDATE</button>' +
      '</div>';
    document.body.appendChild(el);
    el.addEventListener("click", function (e) {
      if (e.target.closest("[data-gt-close]")) closeSheet();
    });
    q(".gt-sc-sheet__go", el).addEventListener("click", confirmSheet);
    sheet = el;
    return el;
  }

  /* THE PANEL TAKES THE CART'S COLUMN ON DESKTOP, and the width is measured
     rather than assumed - the app's drawer is 500px today and that is one of
     its settings. Below the breakpoint the custom property is ignored, so this
     costs one rect read and changes nothing on a phone. */
  function sizeToDrawer() {
    if (!sheet) return;
    var drawer = q("#slidecarthq .slidecarthq");
    if (!drawer) return;
    var w = drawer.getBoundingClientRect().width;
    if (w > 200) sheet.style.setProperty("--gt-sheet-w", Math.round(w) + "px");
  }

  /* Ask what is actually under the button, and get out of its way. See the
     header. Runs on open and on resize, never continuously. */
  function clearBottomChrome() {
    if (!sheet || !sheet.classList.contains("is-open")) return;
    var panel = q(".gt-sc-sheet__panel", sheet);
    var go = q(".gt-sc-sheet__go", sheet);
    if (!panel || !go) return;

    var base = 20, pad = 0, pass = 0;
    var cap = Math.round(window.innerHeight * 0.3);   /* never eat the sheet */
    panel.style.paddingBottom = "";

    /* MEASURE THE CHROME, NOT THE IFRAME IT LIVES IN. First attempt used the
       hit element's own rect and asked for 840px of padding on an 812px
       screen: elementFromPoint returns IFRAME#PBarNextFrame, which spans the
       whole viewport, while the bar a shopper can actually see is the fixed
       DIV#PBarNextFrameWrapper at 744..812. The thing that occupies space is
       the positioned box, so that is the one to measure. */
    function blocker(el) {
      for (var n = el; n && n !== document.body; n = n.parentElement) {
        var pos = getComputedStyle(n).position;
        if (pos === "fixed" || pos === "sticky") return n;
      }
      return el;
    }

    while (pass < 4) {
      var b = go.getBoundingClientRect();
      if (!b.width || !b.height) return;            /* not laid out yet */
      var hit = document.elementFromPoint(b.left + b.width / 2, b.top + b.height / 2);
      if (!hit) return;                             /* off screen, nothing to do */
      if (hit === go || go.contains(hit)) return;   /* reachable, we are done */

      var o = blocker(hit).getBoundingClientRect();
      var need = Math.max(0, window.innerHeight - o.top) + 8;
      if (!(need > pad)) need = pad + 24;           /* unmeasurable: step instead */
      pad = Math.min(need, cap);
      panel.style.paddingBottom = "calc(" + (base + pad) + "px + env(safe-area-inset-bottom, 0px))";
      if (pad >= cap) return;                       /* done what we safely can */
      pass++;
    }
  }

  function closeSheet() {
    gen++;                       /* any open() still queued is now void */
    state = null;
    if (!sheet) return;
    sheet.classList.remove("is-open");
    document.documentElement.classList.remove("gt-sc-sheet-open");
    /* The hide is still deferred so the panel can slide out, but it is gated
       on the ticket rather than on re-reading a class a stale callback may
       have put back. The stylesheet stops the scrim catching anything in the
       meantime. */
    var mine = gen;
    setTimeout(function () {
      if (sheet && gen === mine) sheet.setAttribute("hidden", "hidden");
    }, 300);
  }

  function paintImage() {
    var img = q(".gt-sc-sheet__img", sheet);
    if (!img || !state) return;
    var p = state.product, v = state.variant;
    var src = (v && v.featured_image && v.featured_image.src) || p.featured_image || "";
    var want = sized(src, 400);
    if (img.getAttribute("src") !== want) img.setAttribute("src", want);
    img.setAttribute("alt", (v && v.name) || p.title || "");
    var thumb = q(".gt-sc-sheet__thumb", sheet);
    if (thumb) thumb.style.display = want ? "" : "none";
  }

  /* The shop's flat colour file, with the variant's own photograph behind it
     and a colour keyword behind that. Ordered in the header.

     Measured on the preview, and worth recording because it is not what the
     PDP led us to expect: of the Predator's six colours, five resolve to a
     flat file and BLACK DOES NOT - /cdn/shop/files/black.png is a 404 on this
     shop. So the fallback is not a theoretical nicety, it fires on the most
     common colour in the catalogue, and what the shopper sees there is a
     thumbnail of the gloves themselves. That is the right answer anyway.

     The probe is an Image rather than a fetch so a miss costs nothing beyond
     one 404, and the result is remembered per value for the life of the page
     so opening the sheet a second time re-probes nothing. */
  var swatchSrc = {};

  function paintSwatch(el, val, photo) {
    /* The colour keyword first, because setting it is also how we find out
       whether it is real: the browser drops a value it does not understand, so
       reading the property back distinguishes "green" from "oxblood". */
    var word = handleize(String(val).split(" ").pop());
    if (word) el.style.backgroundColor = word;
    var named = el.style.backgroundColor !== "";

    var flat = "/cdn/shop/files/" + handleize(val) + "_60x60.png";

    /* THE PHOTOGRAPH IS THE LAST RESORT, NOT THE SECOND ONE, and that ordering
       was decided by looking at it. Black has no flat file on this shop, so it
       fell through to the variant's own image - which for the Predator is a
       studio shot on a pale background, and at 36px square it reads as light
       grey. A colour picker showing grey for Black is worse than useless. A
       real keyword is a truer answer than a small photograph of the product;
       the photograph only earns its place when there is no keyword either, as
       on "Urban Rider", where a picture beats a blank tile. */
    var fallback = (!named && photo) ? sized(photo, 120) : "";

    function use(src) { if (src) el.style.backgroundImage = 'url("' + src + '")'; }

    if (swatchSrc[val] !== undefined) { use(swatchSrc[val] || fallback); return; }

    var probe = new Image();
    probe.onload = function () { swatchSrc[val] = flat; use(flat); };
    probe.onerror = function () { swatchSrc[val] = ""; use(fallback); };
    probe.src = flat;
  }

  function paintSheet() {
    if (!state) return;
    var p = state.product, vals = state.vals;

    qa(".gt-sc-val", sheet).forEach(function (el) {
      var i = Number(el.getAttribute("data-gt-i"));
      var val = el.getAttribute("data-gt-v");
      var gone;

      if (state.wide[i]) {
        /* A size rail answers about the product, not about the current
           colour - see the header. */
        gone = !(state.index[i] && state.index[i].alive[val]);
      } else {
        var probe = vals.slice();
        probe[i] = val;
        var t = variantByValues(p, probe);
        gone = !(t && t.available);
      }

      el.classList.toggle("is-on", vals[i] === val);
      el.classList.toggle("is-gone", gone);
      el.setAttribute("aria-checked", vals[i] === val ? "true" : "false");
      /* The upcharge, on the chip, the way the PDP prints it. Written only
         when it changes, because this runs on every tap. A swatch is skipped:
         26px of colour has nowhere to put it - the summary line carries it. */
      var d = deltaFor(i, val);
      if (el.classList.contains("gt-sc-val--chip")) {
        var sm = el.querySelector("small");
        if (d) {
          if (!sm) { sm = document.createElement("small"); el.appendChild(sm); }
          if (sm.textContent !== d) sm.textContent = d;
        } else if (sm) {
          el.removeChild(sm);
        }
      }

      el.setAttribute("aria-label", val + (d ? " " + d : "") + (gone ? " - sold out" : ""));
    });

    var v = variantByValues(p, vals);
    state.variant = v;
    paintImage();

    var picked = vals.join(SEP);

    /* The summary is built rather than assigned, so a colour's upcharge can be
       brass beside its name. It is the only place in the sheet a colour is
       spelled out in words, which is why the swatch rails put it here. */
    var sum = q(".gt-sc-sheet__sum", sheet);
    sum.textContent = "";
    vals.forEach(function (val, i) {
      if (i) sum.appendChild(document.createTextNode(SEP));
      sum.appendChild(document.createTextNode(val));
      var d = state.swatch[i] ? deltaFor(i, val) : "";
      if (!d) return;
      var tag = document.createElement("small");
      tag.textContent = " " + d;
      sum.appendChild(tag);
    });

    /* KNOWN is the fix for the hang - see the header. Until the cart has told
       us what this line is, there is nothing to change and the button must not
       pretend otherwise. */
    var known = !!state.line;
    var same = !!(v && known && v.id === state.line.variant_id);
    var ok = !!(v && v.available) && known && !same;

    /* The status line is never empty, so nothing below it ever moves. A
       failure message wins, then the two unbuyable cases, and otherwise the
       PDP's traffic light. No full stop on any of the three - see the
       header. */
    var note = q(".gt-sc-sheet__note", sheet);
    note.textContent = state.failed ? state.failed
      : (!v ? ("We do not make " + picked)
      : (!v.available ? (picked + " is currently sold out")
      : stockLine(v)));

    var go = q(".gt-sc-sheet__go", sheet);
    go.disabled = !ok || busy;
    go.textContent = busy ? "UPDATING..."
      : (!v ? "UNAVAILABLE" : (!v.available ? "SOLD OUT" : (same ? "NO CHANGE" : "UPDATE")));
  }

  function openSheet(item, p) {
    buildSheet();
    var key = item.getAttribute("data-line-item-key");
    var vid = Number(item.getAttribute("data-variant-id") || 0);

    var cur = null, i;
    for (i = 0; i < p.variants.length; i++) if (p.variants[i].id === vid) cur = p.variants[i];
    if (!cur) return;

    state = {
      product: p,
      vals: cur.options.slice(),
      variant: cur,
      key: key,
      line: null,
      index: valueIndex(p),
      wide: optionNames(p).map(function (n) { return SIZE.test(n); }),
      swatch: optionNames(p).map(function (n) { return COLOUR.test(n); }),
      stock: null,
      delta: null,
      failed: ""
    };

    q(".gt-sc-sheet__title", sheet).textContent = p.title || "";

    var opts = q(".gt-sc-sheet__opts", sheet);
    opts.innerHTML = "";

    optionNames(p).forEach(function (name, idx) {
      var row = state.index[idx];
      if (!row || row.order.length < 2) return;   /* one value is not a choice */

      var swatch = COLOUR.test(name);

      var el = document.createElement("div");
      el.className = "gt-sc-sheet__row";
      el.setAttribute("data-gt-row", String(idx));

      var lab = document.createElement("div");
      lab.className = "gt-sc-sheet__label";
      lab.textContent = name;

      var vals = document.createElement("div");
      vals.className = "gt-sc-sheet__vals gt-sc-sheet__vals--" + (swatch ? "swatch" : "chip");
      vals.setAttribute("role", "radiogroup");
      vals.setAttribute("aria-label", name);

      row.order.forEach(function (val) {
        var b = document.createElement("button");
        b.type = "button";
        b.className = "gt-sc-val " + (swatch ? "gt-sc-val--swatch" : "gt-sc-val--chip");
        b.setAttribute("role", "radio");
        b.setAttribute("data-gt-i", String(idx));
        b.setAttribute("data-gt-v", val);
        if (swatch) paintSwatch(b, val, row.photo[val]);
        else b.textContent = val;
        vals.appendChild(b);
      });

      el.appendChild(lab);
      el.appendChild(vals);
      opts.appendChild(el);
    });

    /* One delegated handler rather than one per value, so a product with five
       colours and eight sizes still costs a single listener. NOTHING IS
       DISABLED, so every tap lands - that is the whole point of the rebuild. */
    opts.onclick = function (e) {
      var b = e.target.closest(".gt-sc-val");
      if (!b || !state) return;
      e.preventDefault();
      state.failed = "";          /* a new choice clears the last failure */
      state.vals[Number(b.getAttribute("data-gt-i"))] = b.getAttribute("data-gt-v");
      paintSheet();
    };

    paintSheet();
    sheet.removeAttribute("hidden");

    /* The class that runs the slide-up has to land in a LATER frame than the
       one that unhides the panel, or the browser has no starting transform to
       animate from and the sheet simply appears. The double frame is the usual
       way to buy that, and it has one failure mode found in testing:
       requestAnimationFrame does not run at all while the tab is hidden, so
       the sheet mounted, reported itself open and never got the class. A
       shopper is looking at their own tab, so this is mostly a harness
       artefact - but a page restored from the back-forward cache lands in the
       same state, and the cost of covering it is one timer. Both paths add the
       same class, and adding it twice is nothing. */
    gen++;
    var mine = gen;
    var open = function () {
      /* A frame the phone delayed, or a tab that came back, must not reopen a
         sheet that has since been closed. That left the page under a
         transparent full-screen layer with no state behind it, which is the
         freeze Joe kept hitting. */
      if (!sheet || gen !== mine) return;
      sizeToDrawer();
      sheet.classList.add("is-open");
      /* The scroll lock goes on WITH the sheet, not before it. Locked with
         nothing on screen is indistinguishable from a frozen page. */
      document.documentElement.classList.add("gt-sc-sheet-open");
      /* After the slide-in, because getBoundingClientRect includes the
         transform and the panel is still off the bottom until it finishes. */
      setTimeout(function () { if (gen === mine) clearBottomChrome(); }, 320);
    };
    requestAnimationFrame(function () { requestAnimationFrame(open); });
    setTimeout(open, 60);

    /* The line's own record - quantity, properties, selling plan - fetched
       while the shopper reads, so confirming costs two calls rather than
       three. The DOM knows the quantity but not the properties, and dropping
       those would change what the customer ordered. */
    fetch("/cart.js", { credentials: "same-origin" })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!state || state.key !== key) return;       /* sheet moved on */
        var i;
        for (i = 0; i < cart.items.length; i++) {
          if (cart.items[i].key === key) { state.line = cart.items[i]; break; }
        }
        /* The key is the app's own data-line-item-key and it has always
           matched in testing, but it is read off markup the app re-renders on
           every cart change, so it can go stale between a change and the
           re-render. Falling back to the variant id finds the same line
           whenever there is only one of it, which is the case that matters:
           without this the button never enables and the sheet looks broken. */
        if (!state.line) {
          for (i = 0; i < cart.items.length; i++) {
            if (cart.items[i].variant_id === vid) { state.line = cart.items[i]; state.key = cart.items[i].key; break; }
          }
        }
        paintSheet();
      })
      .catch(function () {});

    /* The traffic light, fetched alongside. It only affects a line of type,
       so nothing waits on it. */
    stockFor(p.handle).then(function (j) {
      if (!state || state.key !== key) return;
      state.stock = j;
      state.delta = deltaMap(j);
      paintSheet();
    });
  }

  var CONFIRM_TIMEOUT = 12000;

  function confirmSheet() {
    if (!state || busy) return;
    var v = state.variant, line = state.line, key = state.key;
    /* The button is not enabled without a line any more, so this is belt and
       braces rather than the load bearing check it used to be. */
    if (!v || !v.available || !line || v.id === line.variant_id) return;

    busy = true;
    state.failed = "";
    paintSheet();

    var settled = false;

    /* Leave the sheet open so the choice is not lost, say what happened, and
       let the drawer re-render from the server, which is the only thing that
       knows the truth about the cart. */
    function fail(msg) {
      if (settled) return;
      settled = true;
      clearTimeout(bail);
      busy = false;
      if (state) {
        state.failed = msg;
        if (state.product) delete cache[state.product.handle];
        paintSheet();
      }
      refresh();
    }

    function done() {
      if (settled) return;
      settled = true;
      clearTimeout(bail);
      busy = false;
      paintSheet();        /* clears UPDATING... while state still exists */
      closeSheet();
      refresh();
    }

    var bail = setTimeout(function () {
      fail("That is taking longer than it should. Please check your cart before trying again.");
    }, CONFIRM_TIMEOUT);

    var body = { id: v.id, quantity: line.quantity };
    if (line.properties && Object.keys(line.properties).length) body.properties = line.properties;
    if (line.selling_plan_allocation && line.selling_plan_allocation.selling_plan) {
      body.selling_plan = line.selling_plan_allocation.selling_plan.id;
    }

    /* ADD FIRST. add.js enforces availability server side, so a size that sold
       out while the sheet was open fails here and the cart is untouched. */
    fetch("/cart/add.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body)
    })
      .then(function (r) {
        if (!r.ok) throw new Error("add");
        return fetch("/cart/change.js", {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: key, quantity: 0 })
        }).then(function (c) {
          /* PAST THIS POINT THE CART HAS ALREADY CHANGED. A failure here is
             not "nothing happened", it is "you now have both", and saying
             otherwise sends the shopper to checkout with a duplicate. */
          if (!c.ok) throw new Error("change");
          done();
        });
      })
      .catch(function (e) {
        if (e && e.message === "change") {
          fail("Added, but we could not remove the old one - please check your cart.");
        } else {
          fail("We could not change that just now. Your cart has not been changed.");
        }
      });
  }

  /* ---------------------------------------------------------------- */

  function build(item) {
    var vp = q(".variants-p", item);
    if (!vp) return;                                   /* no options on this line */
    var vid = Number(item.getAttribute("data-variant-id") || 0);
    if (!vid) return;

    var existing = q("." + MARK, item);
    if (existing && Number(existing.getAttribute("data-gt-vid")) === vid) return;

    var handle = handleOf(item);
    product(handle).then(function (p) {
      if (!p || !p.variants || p.variants.length < 2) return;  /* nothing to change */
      var old = q("." + MARK, item);
      if (old && old.parentNode) old.parentNode.removeChild(old);

      /* A SPAN, NOT A BUTTON, and that is not a style preference. Measured on
         the preview: the app forces display:flex onto its buttons with
         !important, which makes them block-level, so a <button> here jumped to
         its own line however it was styled - the paragraph went from 21px to
         47px. A span with role and tabindex carries the same semantics to a
         screen reader and inherits none of that. The swatch row on the PDP
         cards already uses this exact pattern for the same reason.

         The values INSIDE the sheet are real buttons, because the sheet is
         appended to <body>, outside #slidecarthq, where the app's rules do not
         reach. The stylesheet still pins their display, cheaply, in case that
         ever stops being true. */
      var a = document.createElement("span");
      a.className = MARK;
      a.setAttribute("role", "button");
      a.setAttribute("tabindex", "0");
      a.setAttribute("data-gt-vid", String(vid));
      a.textContent = linkLabel(p);

      function fire(e) {
        e.preventDefault();
        e.stopPropagation();
        openSheet(item, p);
      }
      a.addEventListener("click", fire);
      /* A span is not activated by the keyboard for free. */
      a.addEventListener("keydown", function (e) {
        if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") fire(e);
      });

      /* ON ITS OWN LINE, DIRECTLY UNDER THE VARIANT STATEMENT - and that is the
         second attempt, kept because the first one is the interesting failure.

         Putting the link INSIDE .variants-p works beautifully on a short
         variant name: measured at 375px, "S - 7.5" - Black" is 87px in a 229px
         paragraph, the label is 114px, and it sat on the same line for nothing.

         Then the test product changed to "L - 8.5" - Waxed Brown" and the link
         vanished. The app gives .variants-p `white-space: nowrap; overflow:
         hidden; text-overflow: ellipsis` - so instead of wrapping, it CLIPPED
         the link and rendered "Waxed Brown..." with our control somewhere off
         the end of the box. Overriding that needs !important on three
         properties of an element the app owns and re-renders, and it would
         break the moment they change their markup.

         So the link takes its own line. Measured cost: about 19px, against the
         39px the dropdown it replaces was costing. Predictable on every product
         name beats clever on some of them. */
      if (vp.nextSibling) vp.parentNode.insertBefore(a, vp.nextSibling);
      else vp.parentNode.appendChild(a);
    });
  }

  /* THE INVARIANT: the sheet is never open without a state behind it.

     If that ever becomes false - a callback that outlived its sheet, an app
     re-render that caught us mid-close, something not yet thought of - the
     page is left under a transparent full-screen layer with nothing inside it
     that works, and the only way out is a reload. That is precisely what Joe
     hit three times, and on Chrome for Android, which defers timers and frames
     far more readily than a desktop, it is easy to hit.

     The generation guard above should make it unreachable. This is the belt to
     that pair of braces: one class check, and a broken page becomes a
     self-healing one. */
  function healSheet() {
    if (sheet && !state && sheet.classList.contains("is-open")) closeSheet();
  }

  function sweep() {
    healSheet();
    qa("#slidecarthq .item").forEach(build);
  }

  var t = null;
  function soon() { clearTimeout(t); t = setTimeout(sweep, 60); }

  function start() {
    sweep();
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && sheet && sheet.classList.contains("is-open")) closeSheet();
    });

    /* A tap is the signal that matters. If the shopper is tapping and the sheet
       is open with nothing behind it, the page is dead under a transparent
       layer and nothing else on the page is going to notice - a frozen page
       produces no mutations, so the observer that drives sweep() never fires.
       Passive and capture, so this never interferes with the tap itself; the
       cost when everything is well is one class check per tap. Worst case the
       shopper loses a single tap instead of the page. */
    ["touchstart", "pointerdown"].forEach(function (ev) {
      document.addEventListener(ev, healSheet, { passive: true, capture: true });
    });
    if (window.MutationObserver) {
      var host = q("#slidecarthq");
      if (host) new MutationObserver(soon).observe(host, { childList: true, subtree: true });
      /* The app mounts #slidecarthq late on some pages. */
      new MutationObserver(function () {
        var r = q("#slidecarthq");
        if (r && !r.__gtObserved) {
          r.__gtObserved = true;
          new MutationObserver(soon).observe(r, { childList: true, subtree: true });
          soon();
        }
      }).observe(document.documentElement, { childList: true, subtree: true });
    }
    [400, 1000, 2500].forEach(function (d) { setTimeout(sweep, d); });
    if (MQ.addEventListener) MQ.addEventListener("change", soon);

    /* A rotated phone has different chrome in the bottom band, and an iOS
       toolbar that hides changes the viewport without a resize on some
       builds - so orientationchange is listened for as well as resize. A
       desktop window drag lands here too, which is where sizeToDrawer earns
       its place. */
    var rt = null;
    function reclear() {
      clearTimeout(rt);
      rt = setTimeout(function () { sizeToDrawer(); clearBottomChrome(); }, 200);
    }
    window.addEventListener("resize", reclear);
    window.addEventListener("orientationchange", reclear);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
